// Shared cards (design 01c): events, purchase lists and polls (design 01a
// 6). Helpers that do not touch the screen: dates in Korea time, labels,
// totals, the purchase item changes shown before the server confirms them,
// and my choices in polls.
import type {
  Card,
  CartSection,
  EventCard,
  FrequentItem,
  Member,
  PollCard,
  PurchaseCard,
  PurchaseGoing,
  PurchaseItem,
} from '../../api/types'

export const KST_OFFSET_MS = 9 * 3600_000
const DAY_MS = 86_400_000
const WEEKDAYS = '일월화수목금토'

// ---- cards in the store ----

/** The newer state of one card (by version). */
export function newerCard(a: Card | undefined, b: Card | undefined): Card | undefined {
  if (!a) return b
  if (!b) return a
  return b.version > a.version ? b : a
}

/** Keeps a card unless an equal or newer one is there already. */
export function putCard(cards: Record<number, Card>, card: Card): Record<number, Card> {
  const old = cards[card.id]
  if (old && old.version >= card.version) return cards
  return { ...cards, [card.id]: card }
}

// ---- Korea time ----

export interface KstParts {
  y: number
  m: number
  d: number
  /** 0 = Sunday */
  wd: number
  h: number
  min: number
}

export function kstParts(ms: number): KstParts {
  const t = new Date(ms + KST_OFFSET_MS)
  return {
    y: t.getUTCFullYear(),
    m: t.getUTCMonth() + 1,
    d: t.getUTCDate(),
    wd: t.getUTCDay(),
    h: t.getUTCHours(),
    min: t.getUTCMinutes(),
  }
}

/** 00:00 Korea time of the day ms falls on. */
export function kstDayStart(ms: number): number {
  return Math.floor((ms + KST_OFFSET_MS) / DAY_MS) * DAY_MS - KST_OFFSET_MS
}

/** A Korea-time wall clock reading as ms (month 1..12). */
export function kstTime(y: number, m: number, d: number, h = 0, min = 0): number {
  return Date.UTC(y, m - 1, d, h, min) - KST_OFFSET_MS
}

const two = (n: number) => String(n).padStart(2, '0')

/** "10/16(금)" */
export function dayLabel(ms: number): string {
  const p = kstParts(ms)
  return `${p.m}/${p.d}(${WEEKDAYS[p.wd]})`
}

/** "08:40" */
export function clockLabel(ms: number): string {
  const p = kstParts(ms)
  return `${two(p.h)}:${two(p.min)}`
}

/** "오후 8:00" (like the chat's times) */
export function koreanClock(minuteOfDay: number): string {
  const h = Math.floor(minuteOfDay / 60)
  const m = minuteOfDay % 60
  const half = h < 12 ? '오전' : '오후'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${half} ${h12}:${two(m)}`
}

/** When an event is: "10/16(금) 08:40 ~ 15:00", "10/16(금) 종일",
    "10/16(금) ~ 10/18(일)". */
export function eventWhen(ev: Pick<EventCard, 'starts_at' | 'ends_at' | 'all_day'>): string {
  const start = dayLabel(ev.starts_at)
  if (ev.all_day) {
    if (ev.ends_at === null || kstDayStart(ev.ends_at) === kstDayStart(ev.starts_at)) return `${start} 종일`
    return `${start} ~ ${dayLabel(ev.ends_at)}`
  }
  const time = `${start} ${clockLabel(ev.starts_at)}`
  if (ev.ends_at === null) return time
  if (kstDayStart(ev.ends_at) === kstDayStart(ev.starts_at)) return `${time} ~ ${clockLabel(ev.ends_at)}`
  return `${time} ~ ${dayLabel(ev.ends_at)} ${clockLabel(ev.ends_at)}`
}

/** "오늘", "내일", "모레", "D-5", or "" for the past. */
export function dDay(ms: number, now: number = Date.now()): string {
  const days = Math.round((kstDayStart(ms) - kstDayStart(now)) / DAY_MS)
  if (days < 0) return ''
  if (days === 0) return '오늘'
  if (days === 1) return '내일'
  if (days === 2) return '모레'
  return `D-${days}`
}

// ---- inputs (<input type="date"> and <input type="time">) ----

/** "2026-10-16" */
export function dateInputValue(ms: number): string {
  const p = kstParts(ms)
  return `${p.y}-${two(p.m)}-${two(p.d)}`
}

/** "08:40" */
export function timeInputValue(ms: number): string {
  return clockLabel(ms)
}

/** A date input (and a time input, or 00:00) as ms; null when not valid. */
export function fromInputs(date: string, time = '00:00'): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const t = /^(\d{2}):(\d{2})$/.exec(time)
  if (!d || !t) return null
  const [y, m, day, h, min] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [number, number, number, number, number]
  if (m < 1 || m > 12 || day < 1 || day > 31 || h > 23 || min > 59) return null
  const ms = kstTime(y, m, day, h, min)
  // 2026-02-31 and the like
  return dateInputValue(ms) === date ? ms : null
}

// ---- reminders ----

export interface ReminderPreset {
  rule: string
  label: string
}

export const REMINDER_PRESETS: ReminderPreset[] = [
  { rule: 'd1@20:00', label: '전날 오후 8:00' },
  { rule: 'd0@08:00', label: '당일 오전 8:00' },
  { rule: 'm60', label: '1시간 전' },
  { rule: 'm10', label: '10분 전' },
]

/** "1시간 전", "전날 오후 8:00", "3일 전 오전 9:00" */
export function reminderLabel(rule: string): string {
  const m = /^m(\d+)$/.exec(rule)
  if (m) {
    const n = Number(m[1])
    if (n % 1440 === 0) return n === 1440 ? '하루 전' : `${n / 1440}일 전`
    if (n % 60 === 0) return `${n / 60}시간 전`
    return `${n}분 전`
  }
  const d = /^d(\d)@(\d{2}):(\d{2})$/.exec(rule)
  if (d) {
    const days = Number(d[1])
    const at = koreanClock(Number(d[2]) * 60 + Number(d[3]))
    if (days === 0) return `당일 ${at}`
    if (days === 1) return `전날 ${at}`
    return `${days}일 전 ${at}`
  }
  return rule
}

// ---- answers ----

export type Answer = 'yes' | 'no' | 'maybe'

export interface RsvpSummary {
  yes: number[]
  no: number[]
  maybe: number[]
  mine: Answer | null
}

export function rsvpSummary(ev: EventCard, meId: number): RsvpSummary {
  const s: RsvpSummary = { yes: [], no: [], maybe: [], mine: null }
  for (const r of ev.rsvps) {
    s[r.answer].push(r.user_id)
    if (r.user_id === meId) s.mine = r.answer
  }
  return s
}

// ---- carts (purchase lists; design 01c 3, 3.4) ----

/** "48,000원" */
export function won(n: number): string {
  return `${n.toLocaleString('ko-KR')}원`
}

/** Where a cart sits in the tensor tab's 쇼핑, in the order shown. */
export const CART_SECTIONS: { section: CartSection; label: string }[] = [
  { section: 'online', label: '인터넷 쇼핑' },
  { section: 'offline', label: '오프라인' },
  { section: 'list', label: '구매목록' },
]

export function sectionLabel(section: CartSection): string {
  return CART_SECTIONS.find((s) => s.section === section)?.label ?? section
}

/** The cart's section (offline when a server did not say). */
export function cartSection(card: Card): CartSection {
  return card.purchase?.section ?? 'offline'
}

/** How many of an item (1 when a server did not say). */
export function itemCount(item: PurchaseItem): number {
  return item.count >= 1 ? item.count : 1
}

/** The price of one (design 01c 3.3: one price, no estimate since v33). */
export function unitPrice(item: PurchaseItem): number | null {
  return item.price
}

export interface PurchaseSummary {
  /** Items on the list (rows). */
  count: number
  bought: number
  /** 합계: count × price over every item with a price (null when none). */
  total: number | null
  /** count × price over the bought (checked) items with a price (null when none). */
  bought_total: number | null
}

export function purchaseSummary(items: PurchaseItem[]): PurchaseSummary {
  let total: number | null = null
  let boughtTotal: number | null = null
  let bought = 0
  for (const i of items) {
    const n = itemCount(i)
    if (i.checked_by !== null) bought++
    if (i.price === null) continue
    total = (total ?? 0) + n * i.price
    if (i.checked_by !== null) boughtTotal = (boughtTotal ?? 0) + n * i.price
  }
  return { count: items.length, bought, total, bought_total: boughtTotal }
}

/** "1,500원", "1,500원 × 3 = 4,500원" ('' without a price). */
export function itemPriceLabel(item: PurchaseItem): string {
  const unit = unitPrice(item)
  if (unit === null) return ''
  const n = itemCount(item)
  return n === 1 ? won(unit) : `${won(unit)} × ${n} = ${won(unit * n)}`
}

/** Sums of carts (or sections), design 01c 3.3: 합계, what the lists say
    (count × price of every item), and 실제 금액, what was really paid. */
export interface CartTotals {
  total: number
  real: number
}

/** A cart's real amount (실제 금액): what the cart says was spent, else
    count × price of the bought (checked) items. The server counts the
    same (cards_cart_amounts). */
export function cartReal(card: Card): number {
  return card.purchase?.spent ?? purchaseSummary(card.purchase?.items ?? []).bought_total ?? 0
}

/** One cart's 합계 and 실제 금액. */
export function cartTotals(card: Card): CartTotals {
  const sum = purchaseSummary(card.purchase?.items ?? [])
  return { total: sum.total ?? 0, real: cartReal(card) }
}

/** The sum over carts. */
export function addTotals(cards: Card[]): CartTotals {
  const out = { total: 0, real: 0 }
  for (const c of cards) {
    const t = cartTotals(c)
    out.total += t.total
    out.real += t.real
  }
  return out
}

/** A cart's name (design 01c 3.5): "261014-1 · 주말 장", or the number
    alone when it has no title. Other cards: the title. */
export function cardName(card: Card): string {
  const number = card.type === 'purchase' ? card.purchase?.number : undefined
  if (!number) return card.title
  return card.title ? `${number} · ${card.title}` : number
}

/** Every item of the cart is bought (and it has one). */
export function allBought(card: Card): boolean {
  const items = card.purchase?.items ?? []
  return items.length > 0 && items.every((i) => i.checked_by !== null)
}

/** The cart's purchase day as 00:00 KST (ms), or null while it is being
    bought (design 01c 3.7). */
export function boughtDay(card: Card): number | null {
  const day = card.purchase?.bought?.day
  return day ? fromInputs(day) : null
}

/** How long a deleted cart can be brought back (design 01c 3.6, the
    server's CART_TRASH_DAYS). */
export const CART_TRASH_DAYS = 30

/** Days (begun) left to bring a deleted cart back: 30 right after it was
    deleted (also when this device's clock is behind the server's), 1 on
    its last day; -1 when it can no longer be. A deleted card's updated_at
    is when it was deleted. */
export function trashDaysLeft(card: Card, now: number = Date.now()): number {
  if (!card.deleted) return -1
  const left = card.updated_at + CART_TRASH_DAYS * DAY_MS - now
  return left <= 0 ? -1 : Math.min(CART_TRASH_DAYS, Math.ceil(left / DAY_MS))
}

// ---- the cart screens: the room's 카트 tab, /carts (design 01c 3.6, 3.7, 3.9) ----

/** "10/14(수)" of a day ("2026-10-14"); the text itself when it is not one. */
export function dayTextLabel(day: string): string {
  const ms = fromInputs(day)
  return ms === null ? day : dayLabel(ms)
}

/** "10/14" of a day ("2026-10-14"), the bought tag's. */
export function shortDayLabel(day: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(day)
  return m ? `${Number(m[1])}/${Number(m[2])}` : day
}

/** The items of a cart not bought (checked) yet. */
export function itemsLeft(card: Card): number {
  return (card.purchase?.items ?? []).filter((i) => i.checked_by === null).length
}

const isCart = (c: Card) => c.type === 'purchase'

/** Carts being bought (alive, not bought), newest first: a cart keeps its
    place while it is changed. */
export function cartsBeingBought(cards: Card[]): Card[] {
  return cards.filter((c) => isCart(c) && !c.deleted && !c.purchase?.bought).sort((a, b) => b.id - a.id)
}

/** Bought carts, the latest purchase day first (the same day: the one
    marked last first). */
export function cartsBought(cards: Card[]): Card[] {
  const day = (c: Card) => c.purchase?.bought?.day ?? ''
  const at = (c: Card) => c.purchase?.bought?.at ?? 0
  return cards
    .filter((c) => isCart(c) && !c.deleted && c.purchase?.bought)
    .sort((a, b) => day(b).localeCompare(day(a)) || at(b) - at(a) || b.id - a.id)
}

/** Deleted carts that can still be brought back, the last deleted first
    (design 01c 3.6; a deleted card's updated_at is when it was deleted). */
export function cartsInTrash(cards: Card[], now: number = Date.now()): Card[] {
  return cards
    .filter((c) => isCart(c) && c.deleted && trashDaysLeft(c, now) >= 1)
    .sort((a, b) => b.updated_at - a.updated_at || b.id - a.id)
}

/** Carts by room, the rooms in the order given (the chat list's); carts of
    rooms not in it are left out (a room I left). Rooms without carts are
    not listed. */
export function cartsByRoom(cards: Card[], rooms: number[]): { roomId: number; carts: Card[] }[] {
  const by = new Map<number, Card[]>()
  for (const c of cards) {
    if (c.room_id === null) continue
    const list = by.get(c.room_id)
    if (list) list.push(c)
    else by.set(c.room_id, [c])
  }
  return rooms.flatMap((roomId) => {
    const carts = by.get(roomId)
    return carts ? [{ roomId, carts }] : []
  })
}

/** Sums by section, and over all three (the tensor tab's 쇼핑). Deleted
    carts are not counted. */
export function shoppingTotals(cards: Card[]): Record<CartSection | 'all', CartTotals> {
  const live = cards.filter((c) => c.type === 'purchase' && !c.deleted)
  return {
    online: addTotals(live.filter((c) => cartSection(c) === 'online')),
    offline: addTotals(live.filter((c) => cartSection(c) === 'offline')),
    list: addTotals(live.filter((c) => cartSection(c) === 'list')),
    all: addTotals(live),
  }
}

// ---- going shopping, big purchases, what the room buys (3rd stage) ----

/** What a new event starts from (a board slot's date, design 01c 2). */
export interface EventDraft {
  title: string
  starts_at: number
  all_day: boolean
  note?: string
}

/** How long the card says "장 보러 가는 중" (the server's GOING_SHOWN_MS). */
export const GOING_SHOWN_MS = 3 * 3600_000

/** Who is going shopping for the list now, or null (after three hours). */
export function goingNow(purchase: PurchaseCard | null | undefined, now: number = Date.now()): PurchaseGoing | null {
  const g = purchase?.going ?? null
  return g !== null && now - g.at < GOING_SHOWN_MS ? g : null
}

/** An item's price to compare: count × price. */
export function itemPrice(item: PurchaseItem): number | null {
  const unit = unitPrice(item)
  return unit === null ? null : unit * itemCount(item)
}

/** Candidates by price, cheapest first; those without one last, in list order. */
export function byPrice(items: PurchaseItem[]): PurchaseItem[] {
  return items
    .map((item, i) => ({ item, i, price: itemPrice(item) }))
    .sort((a, b) => {
      if (a.price === null || b.price === null) return a.price === b.price ? a.i - b.i : a.price === null ? 1 : -1
      return a.price - b.price || a.i - b.i
    })
    .map((x) => x.item)
}

/** "shop.example.com" of a candidate's page ('' when none). */
export function linkHost(link: string): string {
  try {
    return link ? new URL(link).host : ''
  } catch {
    return ''
  }
}

const POLL_OPTION_CHARS = 50

/** Poll options to choose among the candidates: "A사 12인용 · 890,000원"
    (50 characters at most, each once, 20 at most). */
export function pollOptionsFrom(items: PurchaseItem[]): string[] {
  const out: string[] = []
  for (const it of items) {
    const price = itemPrice(it)
    const tail = price !== null ? ` · ${won(price)}` : ''
    const room = POLL_OPTION_CHARS - [...tail].length
    const chars = [...it.name.trim()]
    const name = chars.length > room ? chars.slice(0, room - 1).join('') + '…' : chars.join('')
    const text = name + tail
    if (name && !out.includes(text)) out.push(text)
    if (out.length === 20) break
  }
  return out
}

/** The room's frequent items that are not on the list (names compared
    without the spaces at their ends, ASCII case aside, as the server). */
export function frequentToAdd(frequent: FrequentItem[], items: PurchaseItem[]): FrequentItem[] {
  const key = (s: string) => s.trim().replace(/[A-Z]/g, (c) => c.toLowerCase())
  const have = new Set(items.map((i) => key(i.name)))
  return frequent.filter((f) => !have.has(key(f.name)))
}

/** "10월", or "2025년 12월" for another year. */
export function monthLabel(month: string, now: number = Date.now()): string {
  const [y, m] = month.split('-').map(Number)
  return y === kstParts(now).y ? `${m}월` : `${y}년 ${m}월`
}

/** Fields of an item change (price: -1 clears, assignee: 0 clears). */
export interface ItemPatch {
  checked?: boolean
  assignee_id?: number
  name?: string
  /** How many (1..9999). */
  count?: number
  /** The kind of pack ("1팩", "500g"). */
  qty?: string
  /** The price of one. */
  price?: number
  store?: string
  note?: string
  /** A candidate's page ("" takes it away; the server writes it out its way). */
  link?: string
}

/** How many items a cart holds (cards.h PURCHASE_MAX_ITEMS). */
export const PURCHASE_MAX_ITEMS = 100

/** An item made on this device and not confirmed yet: shown at once under
    a temporary id below zero, sent with others in one request (the cart's
    table, design 01c 3.3). */
export interface NewItem {
  tempId: number
  input: ItemPatch & { name: string }
}

/** An item change sent but not confirmed yet (shown at once). Without kind
    a change of itemId; 'add': new items (adds), 'delete': itemId goes,
    'order': the items in a new order (ids). An itemId below zero is an
    item still being added. */
export interface ItemOp {
  key: string
  cardId: number
  itemId: number
  patch: ItemPatch
  /** The item's version on screen when it was made (stale check). */
  version: number
  /** 'waiting': no connection; sent again when it comes back. */
  status: 'sending' | 'waiting'
  kind?: 'add' | 'delete' | 'order'
  adds?: NewItem[]
  /** 'order': the items as they should stand, top first (temporary ids of
      new items too). Items it does not name keep their order after them. */
  ids?: number[]
  /** On its way now: it takes no more rows. */
  inflight?: boolean
  /** 'add' on its way: the ids the card had when it was sent. A live
      change can bring the new items before the answer does; new ids of
      mine with these names are they, so they are not shown twice. */
  knownIds?: number[]
}

function applyPatch(item: PurchaseItem, patch: ItemPatch, meId: number, now: number): PurchaseItem {
  const out: PurchaseItem = { ...item }
  if (patch.checked !== undefined && patch.checked !== (item.checked_by !== null)) {
    out.checked_by = patch.checked ? meId : null
    out.checked_at = patch.checked ? now : null
  }
  if (patch.assignee_id !== undefined) out.assignee_id = patch.assignee_id > 0 ? patch.assignee_id : null
  if (patch.name !== undefined) out.name = patch.name
  if (patch.count !== undefined) out.count = patch.count
  if (patch.qty !== undefined) out.qty = patch.qty
  if (patch.price !== undefined) out.price = patch.price >= 0 ? patch.price : null
  if (patch.store !== undefined) out.store = patch.store
  if (patch.note !== undefined) out.note = patch.note
  if (patch.link !== undefined) out.link = patch.link
  return out
}

function tempItem(n: NewItem, position: number, meId: number, now: number): PurchaseItem {
  const blank: PurchaseItem = {
    id: n.tempId,
    name: n.input.name,
    count: 1,
    qty: '',
    price: null,
    store: '',
    note: '',
    link: '',
    assignee_id: null,
    checked_by: null,
    checked_at: null,
    position,
    version: 0,
    updated_by: meId,
    updated_at: now,
  }
  return applyPatch(blank, n.input, meId, now)
}

/** The card as it will be once the pending item changes are confirmed
    (in the order they were made: new items at the end, changes, items
    taken away). */
export function withOps(card: Card, ops: ItemOp[], meId: number, now: number = Date.now()): Card {
  const mine = ops.filter((o) => o.cardId === card.id)
  if (mine.length === 0 || !card.purchase) return card
  let items = card.purchase.items
  for (const o of mine) {
    if (o.kind === 'add') {
      let adds = o.adds ?? []
      if (o.inflight && o.knownIds) {
        // A live change can bring the new items before the answer does:
        // the server's new items of mine (beyond what the card had) answer
        // these rows, in order, name by name. Each is shown as its row
        // (temporary id, in the server's place) until the answer names
        // it, so that a row is not shown twice and keeps its key on the
        // screen (a table row that went away and came back would lose the
        // cell being typed in).
        const known = new Set(o.knownIds)
        const newest = o.knownIds.reduce((m, id) => Math.max(m, id), 0)
        const left = [...adds]
        items = items.map((it) => {
          if (it.id <= newest || known.has(it.id) || it.updated_by !== meId) return it
          const i = left.findIndex((n) => n.input.name === it.name)
          if (i < 0) return it
          const [n] = left.splice(i, 1)
          return tempItem(n!, it.position, meId, now)
        })
        adds = left
      }
      let pos = items.reduce((m, it) => Math.max(m, it.position), -1)
      items = [...items, ...adds.map((n) => tempItem(n, ++pos, meId, now))]
    } else if (o.kind === 'delete') {
      items = items.filter((it) => it.id !== o.itemId)
    } else if (o.kind === 'order') {
      items = inOrder(items, o.ids ?? []).map((it, i) => (it.position === i ? it : { ...it, position: i }))
    } else if (items.some((it) => it.id === o.itemId)) {
      items = items.map((it) => (it.id === o.itemId ? applyPatch(it, o.patch, meId, now) : it))
    }
  }
  return { ...card, purchase: { ...card.purchase, items } }
}

/** Ids of the items with changes not confirmed yet (new ones too; a new
    order marks no item). */
export function pendingItemIds(ops: ItemOp[], cardId: number): Set<number> {
  const out = new Set<number>()
  for (const o of ops) {
    if (o.cardId !== cardId || o.kind === 'order') continue
    if (o.kind === 'add') for (const n of o.adds ?? []) out.add(n.tempId)
    else out.add(o.itemId)
  }
  return out
}

/** Items in the order of ids; those ids do not name keep their order after
    them (new on the card since, or not known to the order). */
export function inOrder<T extends { id: number }>(items: T[], ids: number[]): T[] {
  const rank = new Map<number, number>()
  ids.forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i)
  })
  return items
    .map((it, i) => ({ it, i, r: rank.get(it.id) ?? ids.length + i }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.it)
}

/** The ids of items with one moved up (-1) or down (+1); null when it
    cannot move (the cart's ↑ ↓, like the board's). */
export function movedItemIds(items: { id: number }[], id: number, delta: -1 | 1): number[] | null {
  const ids = items.map((it) => it.id)
  const i = ids.indexOf(id)
  const j = i + delta
  if (i < 0 || j < 0 || j >= ids.length) return null
  ;[ids[i], ids[j]] = [ids[j]!, ids[i]!]
  return ids
}

/** The ids of items with one dragged to where another is (PC): before it
    when dragged upwards, after it when dragged downwards; null when
    nothing moves. */
export function draggedItemIds(items: { id: number }[], from: number, to: number): number[] | null {
  const ids = items.map((it) => it.id)
  const i = ids.indexOf(from)
  const j = ids.indexOf(to)
  if (i < 0 || j < 0 || i === j) return null
  ids.splice(i, 1)
  ids.splice(j, 0, from)
  return ids
}

/** What POST .../items/order gets for a wanted order: every item the card
    has, once: the wanted ones that are still there first, then the rest
    in their order (items added meanwhile, by someone else too). */
export function orderToSend(items: { id: number }[], wanted: number[]): number[] {
  return inOrder(items, wanted).map((it) => it.id)
}

/** A new order after the queued ops; it replaces a new order of the same
    cart that is waiting at the end (one request for several moves). */
export function queueOrder(ops: ItemOp[], op: ItemOp): ItemOp[] {
  const last = ops[ops.length - 1]
  if (last && last.kind === 'order' && last.cardId === op.cardId && !last.inflight) {
    return [...ops.slice(0, -1), { ...last, ids: op.ids }]
  }
  return [...ops, { ...op, kind: 'order' }]
}

/** The op that adds a temporary item, when it is not on its way yet. */
function openAdd(ops: ItemOp[], tempId: number): ItemOp | undefined {
  return ops.find((o) => o.kind === 'add' && !o.inflight && (o.adds ?? []).some((n) => n.tempId === tempId))
}

/** A new item's fields once a change is merged in (what clears a field
    is left out). */
function mergeNew(input: NewItem['input'], patch: ItemPatch): NewItem['input'] {
  const out: NewItem['input'] = { ...input, ...patch, name: patch.name ?? input.name }
  if (out.price !== undefined && out.price < 0) delete out.price
  if (out.assignee_id !== undefined && out.assignee_id <= 0) delete out.assignee_id
  if (out.checked === false) delete out.checked
  if (out.link === '') delete out.link
  return out
}

/** New items after the queued ops: joined to the last op when it adds to
    the same card and is not on its way yet (one request), up to a cart's
    worth. */
export function queueAdd(ops: ItemOp[], cardId: number, adds: NewItem[], key: string, waiting: boolean): ItemOp[] {
  const last = ops[ops.length - 1]
  if (
    last &&
    last.kind === 'add' &&
    last.cardId === cardId &&
    !last.inflight &&
    (last.adds?.length ?? 0) + adds.length <= PURCHASE_MAX_ITEMS
  ) {
    return [...ops.slice(0, -1), { ...last, adds: [...(last.adds ?? []), ...adds] }]
  }
  const op: ItemOp = {
    key,
    cardId,
    itemId: 0,
    patch: {},
    version: 0,
    status: waiting ? 'waiting' : 'sending',
    kind: 'add',
    adds,
  }
  return [...ops, op]
}

/** An item change after the queued ops; a new item not sent yet takes it
    into its fields instead. */
export function queueChange(ops: ItemOp[], op: ItemOp): ItemOp[] {
  if (op.itemId < 0) {
    const add = openAdd(ops, op.itemId)
    if (add) {
      return ops.map((o) =>
        o === add
          ? { ...o, adds: o.adds!.map((n) => (n.tempId === op.itemId ? { ...n, input: mergeNew(n.input, op.patch) } : n)) }
          : o,
      )
    }
  }
  return [...ops, op]
}

/** An item taken away after the queued ops; a new item not sent yet just
    goes (with the changes queued for it). */
export function queueDelete(ops: ItemOp[], op: ItemOp): ItemOp[] {
  if (op.itemId < 0) {
    const add = openAdd(ops, op.itemId)
    if (add) {
      const rest = add.adds!.filter((n) => n.tempId !== op.itemId)
      return ops
        .filter((o) => o.itemId !== op.itemId && (o !== add || rest.length > 0))
        .map((o) => (o === add ? { ...o, adds: rest } : o))
    }
  }
  return [...ops, { ...op, kind: 'delete' }]
}

/** The new items of an op were confirmed (temporary id -> id): the op
    goes, and the changes queued after it name the items by their ids. */
export function resolveAdded(ops: ItemOp[], key: string, ids: Map<number, number>): ItemOp[] {
  return ops
    .filter((o) => o.key !== key)
    .map((o) => {
      if (o.kind === 'order' && o.ids) return { ...o, ids: o.ids.map((id) => ids.get(id) ?? id) }
      const real = ids.get(o.itemId)
      return real !== undefined ? { ...o, itemId: real } : o
    })
}

/** The new items of an op were refused: it goes, and so do the changes of
    those items queued after it (a new order forgets them). */
export function dropAdded(ops: ItemOp[], key: string): ItemOp[] {
  const op = ops.find((o) => o.key === key)
  const temp = new Set((op?.adds ?? []).map((n) => n.tempId))
  return ops
    .filter((o) => o.key !== key && (o.kind === 'order' || !temp.has(o.itemId)))
    .map((o) => (o.kind === 'order' && o.ids ? { ...o, ids: o.ids.filter((id) => !temp.has(id)) } : o))
}

// ---- the family calendar ----

/** Weeks (Sunday first) of the days shown for a month: KST day starts. */
export function monthGrid(y: number, m: number): number[][] {
  const first = kstTime(y, m, 1)
  const start = first - kstParts(first).wd * DAY_MS
  const weeks: number[][] = []
  let day = start
  do {
    const week: number[] = []
    for (let i = 0; i < 7; i++) {
      week.push(day)
      day += DAY_MS
    }
    weeks.push(week)
  } while (kstParts(day).m === m)
  return weeks
}

/** The KST day starts of the week (Sunday first, like the month) that
    holds ms. */
export function weekDays(ms: number): number[] {
  const start = kstDayStart(ms) - kstParts(ms).wd * DAY_MS
  return Array.from({ length: 7 }, (_, i) => start + i * DAY_MS)
}

/** "10월 4일 ~ 10월 10일", "12월 27일 ~ 2027년 1월 2일" (a week's days). */
export function weekLabel(days: number[], now: number = Date.now()): string {
  const first = kstParts(days[0] ?? now)
  const last = kstParts(days[days.length - 1] ?? now)
  const year = kstParts(now).y
  const part = (p: KstParts) => `${p.y !== year ? `${p.y}년 ` : ''}${p.m}월 ${p.d}일`
  return `${part(first)} ~ ${part(last)}`
}

/** One day of the week view (design 01c 2.1): whole-day events and timed
    ones going on from an earlier day on top, then those that start that
    day by time. events: each occurrence on its own (happenings). */
export function dayAgenda(events: Card[], day: number): { top: Card[]; timed: Card[] } {
  const top: Card[] = []
  const timed: Card[] = []
  for (const c of events) {
    const ev = c.event
    if (!ev || !eventDays(ev).includes(day)) continue
    if (ev.all_day || kstDayStart(ev.starts_at) !== day) top.push(c)
    else timed.push(c)
  }
  const byStart = (a: Card, b: Card) => a.event!.starts_at - b.event!.starts_at || a.id - b.id
  return { top: top.sort(byStart), timed: timed.sort(byStart) }
}

/** When a new event starts unless changed: the next whole hour, or 09:00
    of another day picked in the calendar. */
export function newEventStart(day: number | null, now: number = Date.now()): number {
  const next = Math.ceil(now / 3_600_000) * 3_600_000
  if (day === null || day === kstDayStart(now)) return next
  return kstDayStart(day) + 9 * 3_600_000
}

/** The KST days (day starts) an event covers, at most `limit`. A timed
    event that ends at 00:00 does not reach into that day. */
export function eventDays(ev: Pick<EventCard, 'starts_at' | 'ends_at' | 'all_day'>, limit = 62): number[] {
  const first = kstDayStart(ev.starts_at)
  let end = ev.ends_at ?? ev.starts_at
  if (!ev.all_day && end > ev.starts_at) end -= 1
  const last = kstDayStart(end)
  const out: number[] = []
  for (let d = first; d <= last && out.length < limit; d += DAY_MS) out.push(d)
  return out
}

// ---- repeating events (design 01c 2; cards.h repeat_next on the server) ----

export type Repeat = EventCard['repeat']

export const REPEAT_LABELS: Record<Repeat, string> = { none: '안 함', weekly: '매주', monthly: '매월', yearly: '매년' }

/** The first time a repeating event starts at or after `from` (its start
    when from <= start), or null. Same rules as the server: the time of day
    stays, the 31st falls on a shorter month's last day, 29 February on the
    28th. */
export function repeatNext(start: number, repeat: Repeat, from: number): number | null {
  if (from <= start) return start
  if (repeat === 'none') return null
  if (repeat === 'weekly') return start + Math.ceil((from - start) / (7 * DAY_MS)) * 7 * DAY_MS
  const s = kstParts(start)
  const f = kstParts(from)
  const tod = start - kstDayStart(start)
  const step = repeat === 'monthly' ? 1 : 12
  const first = s.y * 12 + s.m - 1
  let m = f.y * 12 + f.m - 1 - step
  m = m < first ? first : m - ((m - first) % step)
  for (let i = 0; i < 4; i++, m += step) {
    const y = Math.floor(m / 12)
    const mo = (m % 12) + 1
    const last = new Date(Date.UTC(y, mo, 0)).getUTCDate()
    const at = kstTime(y, mo, Math.min(s.d, last)) + tod
    if (at >= from) return at
  }
  return null
}

/** Each time an event happens that touches [from, to): its start, shifted
    copies of a repeating one. */
export function occurrences<T extends Pick<EventCard, 'starts_at' | 'ends_at' | 'all_day' | 'repeat'>>(
  ev: T,
  from: number,
  to: number,
  limit = 400,
): T[] {
  const length = ev.ends_at === null ? 0 : ev.ends_at - ev.starts_at
  if (ev.repeat === 'none') return [ev]
  const tail = length + (ev.all_day ? DAY_MS - 1 : 0)
  const out: T[] = []
  for (let at = repeatNext(ev.starts_at, ev.repeat, from - tail); at !== null && at < to && out.length < limit; ) {
    out.push({ ...ev, starts_at: at, ends_at: ev.ends_at === null ? null : at + length })
    at = repeatNext(ev.starts_at, ev.repeat, at + 1)
  }
  return out
}

/** A repeating event as it is now or next ("다음" in its card); others as they are. */
export function upcoming<T extends Pick<EventCard, 'starts_at' | 'ends_at' | 'all_day' | 'repeat'>>(
  ev: T,
  now = Date.now(),
): T {
  if (ev.repeat === 'none') return ev
  // one still going on (a whole day: until midnight) counts
  return occurrences(ev, now, Number.MAX_SAFE_INTEGER, 1)[0] ?? ev
}

// ---- polls (design 01a 6) ----

/** My choices in a poll as of a card version. */
export interface PollVotes {
  version: number
  ids: number[]
}

/** Keeps my choices unless newer ones are known. */
export function notePollVotes(
  votes: Record<number, PollVotes>,
  cardId: number,
  version: number,
  ids: number[],
): Record<number, PollVotes> {
  const old = votes[cardId]
  if (old && (old.version > version || (old.version === version && sameIds(old.ids, ids)))) return votes
  return { ...votes, [cardId]: { version, ids } }
}

/** Notes my choices from cards sent to me (the ones with my_votes). */
export function notePollCards(votes: Record<number, PollVotes>, cards: Card[]): Record<number, PollVotes> {
  let out = votes
  for (const c of cards) if (c.poll?.my_votes) out = notePollVotes(out, c.id, c.version, c.poll.my_votes)
  return out
}

function sameIds(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i])
}

/** Open: not closed and its time has not come. */
export function pollOpen(poll: PollCard, now = Date.now()): boolean {
  return poll.closed_at === null && (poll.closes_at === null || poll.closes_at > now)
}

/** "오늘 18:00", "내일 09:30", "10/16(금) 18:00" */
export function whenLabel(ms: number, now = Date.now()): string {
  const days = Math.round((kstDayStart(ms) - kstDayStart(now)) / DAY_MS)
  const day = days === 0 ? '오늘' : days === 1 ? '내일' : dayLabel(ms)
  return `${day} ${clockLabel(ms)}`
}

/** Share of the people who voted, in whole percent. */
export function pollPercent(count: number, voters: number): number {
  return voters === 0 ? 0 : Math.round((count * 100) / voters)
}

/** The options with the most votes (none before anyone voted). */
export function pollLeaders(poll: PollCard): number[] {
  const most = Math.max(0, ...poll.options.map((o) => o.count))
  return most === 0 ? [] : poll.options.filter((o) => o.count === most).map((o) => o.id)
}

/** Members who see the poll (joined before it) and have not voted; only
    when it is not anonymous. */
export function notVoted(poll: PollCard, members: Member[], seq: number): number[] {
  if (poll.anonymous) return []
  const voted = new Set(poll.options.flatMap((o) => o.user_ids ?? []))
  return members.filter((m) => m.joined_seq < seq && !voted.has(m.user_id)).map((m) => m.user_id)
}
